import axios from 'axios';
import type {
  MotivationDiscipline,
  MotivationEvidenceScope,
  MotivationFact,
  MotivationFactCode,
  MotivationFactCollection,
  MotivationFactValue,
  MotivationRow,
  MotivationValueKind,
} from '../types/motivation';
import { MOTIVATION_P1A_FACT_CODES } from '../types/motivation';
import { apiClient } from './api';

const allowedCodes = new Set<string>(MOTIVATION_P1A_FACT_CODES);
const disciplines = new Set<string>(['SINGLES', 'DOUBLES']);
const scopes = new Set<string>(['VERIFIED_ONLY', 'CASUAL_ONLY', 'COMBINED_LABELED']);
const kinds = new Set<string>(['COUNT', 'TIERED_COUNT', 'RATIO', 'RECORD', 'TREND', 'BEST']);

const expectedKind: Record<MotivationFactCode, Exclude<MotivationValueKind, 'TIERED_COUNT'>> = {
  MATCHES_PLAYED: 'COUNT',
  MATCHES_WON: 'COUNT',
  GAMES_WON: 'COUNT',
  GAME_SHARE: 'RATIO',
  GAME_SHARE_VS_OWN_TRAILING_MEAN: 'TREND',
  CLOSE_SET_RECORD: 'RECORD',
  TIEBREAK_RECORD: 'RECORD',
  COMEBACK_COUNT: 'COUNT',
  PERSONAL_BEST_GAME_SHARE_VS_OPPONENT: 'BEST',
};

const order: MotivationFactCode[] = [
  'MATCHES_PLAYED', 'MATCHES_WON', 'GAMES_WON', 'GAME_SHARE',
  'GAME_SHARE_VS_OWN_TRAILING_MEAN', 'CLOSE_SET_RECORD', 'TIEBREAK_RECORD',
  'COMEBACK_COUNT', 'PERSONAL_BEST_GAME_SHARE_VS_OPPONENT',
];

const labels: Record<MotivationFactCode, string> = {
  MATCHES_PLAYED: 'Matches played',
  MATCHES_WON: 'Matches won',
  GAMES_WON: 'Games won',
  GAME_SHARE: 'Game share',
  GAME_SHARE_VS_OWN_TRAILING_MEAN: 'Recent game-share trend',
  CLOSE_SET_RECORD: 'Close-set record',
  TIEBREAK_RECORD: 'Tiebreak record',
  COMEBACK_COUNT: 'Comebacks',
  PERSONAL_BEST_GAME_SHARE_VS_OPPONENT: 'Best game share',
};

export async function getMyMotivationFacts(): Promise<MotivationFactCollection> {
  const response = await apiClient.get<unknown>('/api/v1/web/motivation/me');
  return decodeMotivationFactCollection(response.data);
}

export function isMotivationNotFound(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

export function decodeMotivationFactCollection(input: unknown): MotivationFactCollection {
  const root = object(input, 'motivation response');
  if (!Array.isArray(root.items)) throw invalid('items');
  const items: MotivationFact[] = [];
  for (const rawItem of root.items) {
    const item = object(rawItem, 'item');
    if (item.kind !== 'FACT') continue;
    const rawFact = object(item.fact, 'fact');
    if (typeof rawFact.factCode !== 'string' || !allowedCodes.has(rawFact.factCode)) continue;
    items.push(decodeFact(rawFact, rawFact.factCode as MotivationFactCode));
  }
  return { items };
}

export function availableMotivationDisciplines(facts: MotivationFact[]): MotivationDiscipline[] {
  return (['SINGLES', 'DOUBLES'] as const).filter(discipline =>
    facts.some(fact => fact.facet.discipline === discipline));
}

export function buildMotivationRows(
  facts: MotivationFact[],
  discipline: MotivationDiscipline,
): MotivationRow[] {
  const selected = facts.filter(fact => fact.facet.discipline === discipline);
  return order.flatMap(code => {
    const facets = selected.filter(fact => fact.factCode === code);
    return facets.length ? [toRow(code, facets)] : [];
  });
}

export function hasMotivationReconciliationNotice(
  facts: MotivationFact[],
  discipline: MotivationDiscipline,
): boolean {
  return facts.some(fact => fact.facet.discipline === discipline && fact.reconciliationNotice);
}

function decodeFact(raw: Record<string, unknown>, factCode: MotivationFactCode): MotivationFact {
  const facet = object(raw.facet, 'facet');
  const discipline = enumValue(facet.discipline, disciplines, 'discipline') as MotivationDiscipline;
  const evidenceScope = enumValue(facet.evidenceScope, scopes, 'evidenceScope') as MotivationEvidenceScope;
  const valueKind = enumValue(facet.valueKind, kinds, 'valueKind') as MotivationValueKind;
  const requiredKind = evidenceScope === 'COMBINED_LABELED' ? 'TIERED_COUNT' : expectedKind[factCode];
  if (valueKind !== requiredKind || (valueKind === 'TIERED_COUNT' && expectedKind[factCode] !== 'COUNT')) {
    throw invalid(`${factCode} valueKind`);
  }
  const subject = object(raw.subject, 'subject');
  if (subject.label !== 'You' || !Array.isArray(subject.participants) || subject.participants.length !== 0) {
    throw invalid('private subject');
  }
  return {
    factCode,
    title: text(raw.title, 'title'),
    facet: { discipline, evidenceScope, valueKind },
    subject: { label: 'You', participants: [] },
    value: decodeValue(object(raw.value, 'value'), valueKind),
    reconciliationNotice: raw.reconciliationNotice == null
      ? null : text(raw.reconciliationNotice, 'reconciliationNotice'),
  };
}

function decodeValue(raw: Record<string, unknown>, kind: MotivationValueKind): MotivationFactValue {
  switch (kind) {
    case 'COUNT': return { count: integer(raw.count, 'count'), unit: text(raw.unit, 'unit') };
    case 'TIERED_COUNT': return {
      verifiedCount: integer(raw.verifiedCount, 'verifiedCount'),
      casualCount: integer(raw.casualCount, 'casualCount'),
      unit: text(raw.unit, 'unit'),
    };
    case 'RATIO': return { value: finite(raw.value, 'value') };
    case 'RECORD': {
      const wins = integer(raw.wins, 'wins');
      const losses = integer(raw.losses, 'losses');
      const ties = integer(raw.ties, 'ties');
      const matches = integer(raw.matches, 'matches');
      if (matches !== wins + losses + ties) throw invalid('record');
      return { wins, losses, ties, matches };
    }
    case 'TREND': return {
      current: finite(raw.current, 'current'),
      baseline: finite(raw.baseline, 'baseline'),
      delta: finite(raw.delta, 'delta'),
      baselineSampleCount: integer(raw.baselineSampleCount, 'baselineSampleCount'),
    };
    case 'BEST': {
      const counterparty = object(raw.counterparty, 'counterparty');
      if (!Array.isArray(counterparty.participants) || counterparty.participants.length !== 0) {
        throw invalid('counterparty participants');
      }
      return {
        value: finite(raw.value, 'value'),
        achievedPeriod: text(raw.achievedPeriod, 'achievedPeriod'),
        counterparty: { label: text(counterparty.label, 'counterparty label'), participants: [] },
      };
    }
  }
}

function toRow(code: MotivationFactCode, facts: MotivationFact[]): MotivationRow {
  const combined = facts.find(fact => fact.facet.evidenceScope === 'COMBINED_LABELED');
  const scoped = (['VERIFIED_ONLY', 'CASUAL_ONLY'] as const)
    .map(scope => facts.find(fact => fact.facet.evidenceScope === scope))
    .filter((fact): fact is MotivationFact => Boolean(fact));
  const rendered = combined
    ? formatTiered(combined.value)
    : scoped.map(fact => `${scopeLabel(fact.facet.evidenceScope)} ${formatValue(code, fact.value)}`).join(' · ');
  const best = facts.find(fact => fact.facet.valueKind === 'BEST');
  return {
    code,
    section: section(code),
    label: labels[code],
    value: rendered,
    detail: best && 'achievedPeriod' in best.value
      ? `${best.value.counterparty.label} · ${best.value.achievedPeriod}` : undefined,
  };
}

function formatTiered(value: MotivationFactValue): string {
  if (!('verifiedCount' in value)) throw invalid('tiered presentation');
  return `${value.verifiedCount} Verified · ${value.casualCount} Casual`;
}

function formatValue(code: MotivationFactCode, value: MotivationFactValue): string {
  if ('count' in value) return String(value.count);
  if ('wins' in value) return `${value.wins}W · ${value.losses}L${value.ties ? ` · ${value.ties}T` : ''}`;
  if ('current' in value) {
    const delta = Math.abs(value.delta) < 0.000000000001 ? 0 : value.delta * 100;
    const deltaText = `${delta > 0 ? '+' : ''}${Number.isInteger(delta) ? delta.toFixed(0) : delta.toFixed(1)} pp`;
    return `${formatPercent(value.current)} · ${deltaText}`;
  }
  if ('value' in value) return formatPercent(value.value);
  throw invalid(`${code} presentation`);
}

function formatPercent(value: number): string {
  const percent = Math.abs(value) < 0.000000000001 ? 0 : value * 100;
  const rounded = Math.round(percent * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
}

function scopeLabel(scope: MotivationEvidenceScope): string {
  return scope === 'VERIFIED_ONLY' ? 'Verified' : 'Casual';
}

function section(code: MotivationFactCode): MotivationRow['section'] {
  if (['MATCHES_PLAYED', 'MATCHES_WON', 'GAMES_WON', 'GAME_SHARE'].includes(code)) return 'Record';
  if (code === 'PERSONAL_BEST_GAME_SHARE_VS_OPPONENT') return 'Personal best';
  return 'Patterns';
}

function object(value: unknown, name: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw invalid(name);
  return value as Record<string, unknown>;
}

function text(value: unknown, name: string): string {
  if (typeof value !== 'string' || !value.trim()) throw invalid(name);
  return value.trim();
}

function finite(value: unknown, name: string): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) throw invalid(name);
  return Object.is(parsed, -0) ? 0 : parsed;
}

function integer(value: unknown, name: string): number {
  const parsed = finite(value, name);
  if (!Number.isInteger(parsed) || parsed < 0) throw invalid(name);
  return parsed;
}

function enumValue(value: unknown, allowed: Set<string>, name: string): string {
  if (typeof value !== 'string' || !allowed.has(value)) throw invalid(name);
  return value;
}

function invalid(name: string): Error {
  return new Error(`INVALID_MOTIVATION_RESPONSE:${name}`);
}
